import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AccountAction,
  DataRequestStatus,
  DataRequestType,
  DataRequestVia,
  DataRequestDto,
  NoteDto,
  UserExportResponse,
  UserOverviewResponse,
  RoleExtensionAction,
  UserDetailResponse,
  UserDto,
  UsersListParams,
  UsersListResponse,
  UserRoleType,
  VerificationDecisionAction,
  VerificationType,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/users`;

function toQuery(params: UsersListParams): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if (params.q) search.set("q", params.q);
  if (params.account_status) search.set("account_status", params.account_status);
  if (params.email) search.set("email", params.email);
  if (params.phone) search.set("phone", params.phone);
  if (params.public_id) search.set("public_id", params.public_id);
  if (params.display_name) search.set("display_name", params.display_name);
  if (params.market_id) search.set("market_id", params.market_id);
  if (params.sport) search.set("sport", params.sport);
  if (params.tier) search.set("tier", params.tier);
  if (params.verified) search.set("verified", params.verified);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createUsersApi(client: ApiClient) {
  return {
    list(params: UsersListParams = {}) {
      return client.get<UsersListResponse>(`${BASE}${toQuery(params)}`);
    },

    getDetail(userId: string) {
      return client.get<UserDetailResponse>(`${BASE}/${userId}`);
    },

    updateProfile(
      userId: string,
      body: {
        display_name?: string | null;
        email?: string | null;
        phone?: string | null;
        market_id?: string | null;
        profile_data?: Record<string, unknown>;
      },
    ) {
      return client.patch<{ user: UserDto; meta: UsersListResponse["meta"] }>(
        `${BASE}/${userId}`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAccountAction(
      userId: string,
      action: AccountAction,
      body: { reason: string; approval_id?: string },
    ) {
      return client.post<{ user: UserDto; meta: UsersListResponse["meta"] }>(
        `${BASE}/${userId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    createRoleExtension(
      userId: string,
      body: {
        role_type: UserRoleType;
        status?: string;
        effective_from?: string;
        effective_to?: string | null;
        source_type?: string | null;
        source_id?: string | null;
      },
    ) {
      return client.post(
        `${BASE}/${userId}/role-extensions`,
        body,
        newIdempotencyKey(),
      );
    },

    applyRoleExtensionAction(
      userId: string,
      roleExtensionId: string,
      action: RoleExtensionAction,
      body: { reason: string },
    ) {
      return client.post(
        `${BASE}/${userId}/role-extensions/${roleExtensionId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    requestVerification(
      userId: string,
      body: {
        verification_type?: VerificationType;
        expires_at?: string | null;
      } = {},
    ) {
      return client.post(
        `${BASE}/${userId}/verifications`,
        body,
        newIdempotencyKey(),
      );
    },

    decideVerification(
      userId: string,
      verificationId: string,
      body: {
        action: VerificationDecisionAction;
        decision_reason: string;
        approval_id?: string;
        is_override?: boolean;
      },
    ) {
      return client.post(
        `${BASE}/${userId}/verifications/${verificationId}/actions/decide`,
        body,
        newIdempotencyKey(),
      );
    },

    mergeUsers(body: {
      survivor_user_id: string;
      duplicate_user_id: string;
      reason: string;
      approval_id: string;
    }) {
      return client.post(`${BASE}/actions/merge`, body, newIdempotencyKey());
    },

    exportUser(userId: string) {
      return client.post<UserExportResponse>(
        `${BASE}/${userId}/actions/export`,
        {},
        newIdempotencyKey(),
      );
    },

    getOverview(userId: string) {
      return client.get<UserOverviewResponse>(`${BASE}/${userId}/overview`);
    },

    sportFacets() {
      return client.get<{ sports: Array<{ sport: string; players: number }> }>(
        `${BASE}/facets/sports`,
      );
    },

    addNote(userId: string, body: { body: string; pinned?: boolean }) {
      return client.post<{ note: NoteDto }>(
        `${BASE}/${userId}/notes`,
        body,
        newIdempotencyKey(),
      );
    },

    pinNote(userId: string, noteId: string, pinned: boolean) {
      return client.post(
        `${BASE}/${userId}/notes/${noteId}/actions/pin`,
        { pinned },
        newIdempotencyKey(),
      );
    },

    createDataRequest(
      userId: string,
      body: {
        request_type: DataRequestType;
        received_via: DataRequestVia;
        received_at?: string;
        due_at?: string | null;
      },
    ) {
      return client.post<{ data_request: DataRequestDto }>(
        `${BASE}/${userId}/data-requests`,
        body,
        newIdempotencyKey(),
      );
    },

    updateDataRequest(
      userId: string,
      requestId: string,
      body: { status: Exclude<DataRequestStatus, "received">; note?: string | null },
    ) {
      return client.post<{ data_request: DataRequestDto }>(
        `${BASE}/${userId}/data-requests/${requestId}/actions/update`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type UsersApi = ReturnType<typeof createUsersApi>;
